import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  Chip,
  Paper,
  Popover,
  Tooltip,
  InputAdornment,
  Modal,
  Badge,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import SourceOutlinedIcon from "@mui/icons-material/SourceOutlined";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import MicNoneIcon from "@mui/icons-material/MicNone";
import StopIcon from "@mui/icons-material/Stop";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import color from "../../Constants/colors";
import theme from "../../Constants/theme";
import {
  useAccessibleComoAgents,
  useSingleComoConversation,
  useCreateEmptyComoConversation,
  useGenerateComoMessage,
  useComoTools,
  useComoSteps,
  useAccessibleEntities,
  useConversationDocuments,
  useUploadConversationDocument,
  useDeleteConversationDocument,
  useLibraryQuota,
  useTranscribeAudio,
} from "../../Query/useComoAgent";
import { getConversationMessages } from "../../Api/comoAgent";
import { useKnowledgeList } from "../../Query/useKnowledge";
import BuildIcon from "@mui/icons-material/Build";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HubIcon from "@mui/icons-material/Hub";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useLLMModels } from "../../Query/useLLM";
import { ComoAgent, ComoMessage, DebugInfo } from "../../Models/comoAgent";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { getModelIcon } from "../../Helpers/modelHelpers";
import ChatMarkdown from "../Common/ChatMarkdown";
import LibraryView from "../Library/LibraryView";
import { ComoInput } from "../Common/ComoInput";
import {
  AgentIcon,
  getToolPresentation,
  slugify,
} from "../../Helpers/toolPresentation";
import { getFileTypeStyle } from "../../Helpers/fileTypePresentation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import UserMemory from "../Profile/UserMemory";
import { useAudioRecorder } from "../../Hooks/useAudioRecorder";
import MyScheduledTasks from "../ScheduledTasks/MyScheduledTasks";
import { useSidebarContextNav } from "../../Contexts/SidebarNavContext";
import ComoExpertsSidebarNav from "./ComoExpertsSidebarNav";
import { VTM_PATH } from "../DashboardMenu/navigationItems";

const VOICE_WAVE_BARS = [
  10, 14, 19, 24, 20, 15, 11, 16, 22, 26, 21, 16, 12, 17, 23, 19, 14, 10,
];

const ComoExpertsChat = () => {
  const { conversationId: conversationIdParam } = useParams<{
    conversationId: string;
  }>();
  const conversationId = conversationIdParam
    ? Number(conversationIdParam)
    : null;
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t, i18n } = useTranslation("comoExperts");
  const sidebarSlot = useSidebarContextNav({
    path: VTM_PATH,
    label: t("dashboard.virtualTeamMembers", { ns: "dashboard" }),
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedMessages, setExpandedMessages] = useState<
    Record<string, boolean>
  >({});
  const [selectedAgent, setSelectedAgent] = useState<ComoAgent | null>(null);
  // `?agent=<id>` opens a new chat with that agent (home dashboard search).
  const [searchParams] = useSearchParams();
  const agentParam = conversationId ? null : searchParams.get("agent");
  const [appliedAgentParam, setAppliedAgentParam] = useState<string | null>(
    null,
  );
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  // The run this tab is waiting on, tied to the conversation it was sent from
  // (null = new chat), so switching conversations doesn't carry it along.
  const [pendingRun, setPendingRun] = useState<{
    conversationId: number | null;
    runId: string;
  } | null>(null);
  const viewedConversationIdRef = useRef(conversationId);
  const isTyping =
    pendingRun !== null && pendingRun.conversationId === conversationId;
  const activeRunId = isTyping ? pendingRun.runId : null;
  const [debugData, setDebugData] = useState<Record<number, DebugInfo>>({});
  const [debugMode, setDebugMode] = useState(false);
  const [reasoningExpanded, setReasoningExpanded] = useState<
    Record<number, boolean>
  >({});
  const [referencesExpanded, setReferencesExpanded] = useState<
    Record<number, boolean>
  >({});
  const [localMessages, setLocalMessages] = useState<ComoMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [modelAnchor, setModelAnchor] = useState<HTMLElement | null>(null);
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [toolMoreAnchor, setToolMoreAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [agentMemoryOpen, setAgentMemoryOpen] = useState(false);
  const [scheduledTasksOpen, setScheduledTasksOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef(0);
  const skipScrollToBottomRef = useRef(false);
  const messageInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );
  const examplePromptsRef = useRef<HTMLDivElement | null>(null);
  const { data: agents = [] } = useAccessibleComoAgents();
  const {
    data: conversationDetail,
    isLoading: isConversationLoading,
    isSuccess: isConversationSuccess,
  } = useSingleComoConversation(conversationId || 0);
  // A run this tab did not start, or whose request was lost, is still running.
  const isServerRunActive =
    !isTyping && Boolean(conversationId && conversationDetail?.is_generating);
  const isAgentBusy = isTyping || isServerRunActive;
  const stepsRunId =
    activeRunId ??
    (isServerRunActive ? (conversationDetail?.active_run_id ?? null) : null);
  const createChatConversationMutation = useCreateEmptyComoConversation();
  const generateMessageMutation = useGenerateComoMessage();
  const { data: reasoningSteps = [] } = useComoSteps(stepsRunId, isAgentBusy);
  const latestStep = reasoningSteps[reasoningSteps.length - 1];
  const { data: llmData } = useLLMModels();
  const { data: allTools = [] } = useComoTools();
  const { data: allKnowledgeBases = [] } = useKnowledgeList();
  const { data: accessibleEntities = [] } = useAccessibleEntities();
  const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>([]);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { data: conversationDocuments = [] } = useConversationDocuments(
    conversationId || 0,
  );
  const uploadDocumentMutation = useUploadConversationDocument();
  const deleteDocumentMutation = useDeleteConversationDocument();
  const { data: libraryQuota } = useLibraryQuota();
  const isLibraryOverLimit = Boolean(
    libraryQuota &&
    libraryQuota.limit !== -1 &&
    libraryQuota.consumed >= libraryQuota.limit,
  );
  const createEmptyConversationMutation = useCreateEmptyComoConversation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [stagedImages, setStagedImages] = useState<
    { name: string; dataUrl: string }[]
  >([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageAttachClick = () => {
    imageInputRef.current?.click();
  };

  const audioRecorder = useAudioRecorder();
  const transcribeAudioMutation = useTranscribeAudio();

  const handleVoiceClick = async () => {
    if (!audioRecorder.isRecording) {
      const started = await audioRecorder.start();
      if (!started) {
        enqueueSnackbar(t("comoExperts.voice.micUnavailable"), {
          variant: "error",
        });
      }
      return;
    }

    const recording = await audioRecorder.stop();
    if (!recording) return;

    transcribeAudioMutation.mutate(
      {
        audio: recording.blob,
        filename: recording.filename,
        language: i18n.language.startsWith("de") ? "de" : "en",
      },
      {
        onSuccess: (text) => {
          if (!text) return;
          setMessage((previous) =>
            previous.trim() ? `${previous.trim()} ${text}` : text,
          );
          messageInputRef.current?.focus();
        },
      },
    );
  };

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!loadMoreRef.current || !messagesScrollRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];

        if (target.isIntersecting && hasMoreMessages && !loadingOlderMessages) {
          handleLoadOlderMessages();
        }
      },
      {
        root: messagesScrollRef.current,
        threshold: 1.0,
      },
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasMoreMessages, loadingOlderMessages]);

  const stageImageFiles = (files: File[]) => {
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setStagedImages((prev) => [
          ...prev,
          { name: file.name, dataUrl: reader.result as string },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    stageImageFiles(files);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (!canAttachImages) return;
    const imageFiles = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (imageFiles.length === 0) return;
    event.preventDefault();
    stageImageFiles(imageFiles);
  };

  const handleRemoveStagedImage = (index: number) => {
    setStagedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (isLibraryOverLimit) return;

    if (conversationId) {
      uploadDocumentMutation.mutate({ conversationId, file });
      return;
    }

    if (!selectedAgent) return;

    createEmptyConversationMutation.mutate(selectedAgent.id, {
      onSuccess: (conversation) => {
        navigate(`/como-experts/${conversation.id}`);
        uploadDocumentMutation.mutate({
          conversationId: conversation.id,
          file,
        });
      },
    });
  };

  const handleRemoveDocument = (documentId: number) => {
    if (!conversationId) return;
    deleteDocumentMutation.mutate({ conversationId, documentId });
  };

  const toggleMessage = (id: number) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (isConversationSuccess && conversationDetail && conversationId) {
      setLocalMessages(conversationDetail.messages || []);
      setHasMoreMessages(conversationDetail.has_more_messages ?? false);
      const agent = agents.find((a) => a.id === conversationDetail.agent_id);
      if (agent) {
        setSelectedAgent(agent);
        if (selectedModelId === null) {
          const accessibleIds = llmData?.accessible_model_ids || [];
          if (
            agent.default_model !== null &&
            accessibleIds.includes(agent.default_model)
          ) {
            setSelectedModelId(agent.default_model);
          } else if (accessibleIds.length > 0) {
            setSelectedModelId(accessibleIds[0]);
          }
        }
      }
    } else if (!conversationId) {
      setLocalMessages([]);
      setHasMoreMessages(false);
    }
  }, [
    isConversationSuccess,
    conversationDetail,
    conversationId,
    agents,
    llmData,
  ]);

  useEffect(() => {
    if (accessibleEntities.length > 0 && selectedEntityIds.length === 0) {
      setSelectedEntityIds(accessibleEntities.map((e) => e.id));
    }
  }, [accessibleEntities]);

  // When older messages are prepended, keep the viewport anchored to the
  // message the user was reading instead of jumping to the bottom.
  useLayoutEffect(() => {
    if (skipScrollToBottomRef.current && messagesScrollRef.current) {
      const el = messagesScrollRef.current;
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    }
  }, [localMessages]);

  useEffect(() => {
    if (skipScrollToBottomRef.current) {
      skipScrollToBottomRef.current = false;
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages]);

  const handleLoadOlderMessages = async () => {
    if (!conversationId || loadingOlderMessages || localMessages.length === 0) {
      return;
    }
    const oldestId = localMessages[0]?.id;
    if (oldestId == null) return;

    setLoadingOlderMessages(true);
    try {
      const page = await getConversationMessages(conversationId, oldestId);
      if (page.messages.length > 0) {
        prevScrollHeightRef.current =
          messagesScrollRef.current?.scrollHeight ?? 0;
        skipScrollToBottomRef.current = true;
        setLocalMessages((prev) => [...page.messages, ...prev]);
      }
      setHasMoreMessages(page.has_more);
    } finally {
      setLoadingOlderMessages(false);
    }
  };

  useEffect(() => {
    if (!selectedAgent && !conversationId) return;
    const id = window.setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [selectedAgent, conversationId]);

  useEffect(() => {
    viewedConversationIdRef.current = conversationId;
  }, [conversationId]);

  // Refocus input once the agent finishes responding
  useEffect(() => {
    if (isAgentBusy) return;
    if (!selectedAgent && !conversationId) return;
    const id = window.setTimeout(() => {
      messageInputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(id);
  }, [isAgentBusy, selectedAgent, conversationId]);

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);

    if (diffSec < 60)
      return t("comoExperts.time.justNow", { defaultValue: "Just now" });
    if (diffMin < 60)
      return t("comoExperts.time.minutesAgo", {
        count: diffMin,
        defaultValue: `${diffMin}m ago`,
      });
    if (diffHour < 24)
      return t("comoExperts.time.hoursAgo", {
        count: diffHour,
        defaultValue: `${diffHour}h ago`,
      });
    if (diffDay < 7)
      return t("comoExperts.time.daysAgo", {
        count: diffDay,
        defaultValue: `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`,
      });

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  const formatFullDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleNewChat = () => {
    setSelectedAgent(null);
    setSelectedModelId(null);
    setSelectedEntityIds(accessibleEntities.map((e) => e.id));
    setLocalMessages([]);
    setMessage("");
    navigate("/como-experts");
  };

  const handleClearAgentSelection = () => {
    setSelectedAgent(null);
    setMessage("");
  };

  const handleSelectAgent = (agent: ComoAgent) => {
    setSelectedAgent(agent);
    const accessibleIds = llmData?.accessible_model_ids || [];
    if (
      agent.default_model !== null &&
      accessibleIds.includes(agent.default_model)
    ) {
      setSelectedModelId(agent.default_model);
    } else if (accessibleIds.length > 0) {
      setSelectedModelId(accessibleIds[0]);
    }
  };

  // Applied once agents and models have loaded, so the default model is set.
  if (
    agentParam &&
    agentParam !== appliedAgentParam &&
    agents.length > 0 &&
    llmData
  ) {
    setAppliedAgentParam(agentParam);
    const requestedAgent = agents.find((a) => a.id === Number(agentParam));
    if (requestedAgent) handleSelectAgent(requestedAgent);
  }

  useEffect(() => {
    if (conversationId) return;
    if (!selectedAgent?.example_messages?.length) return;
    const id = window.setTimeout(() => {
      examplePromptsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [selectedAgent?.id, conversationId]);

  const handleSelectConversation = (id: number) => {
    setSelectedEntityIds(accessibleEntities.map((e) => e.id));
    navigate(`/como-experts/${id}`);
  };

  const handleSendMessage = async () => {
    if (!message.trim() && stagedImages.length === 0) return;
    if (isAgentBusy) return;
    if (!conversationId && !selectedAgent) return;

    const content = message;
    const images = stagedImages.map((img) => img.dataUrl);
    const modelId = selectedModelId;
    const runId = crypto.randomUUID();
    const clearPendingRun = () =>
      setPendingRun((prev) => (prev?.runId === runId ? null : prev));
    const userMsg: ComoMessage = {
      id: Date.now(),
      conversation_id: conversationId ?? 0,
      role: "user",
      content,
      tool_calls: null,
      tool_call_id: null,
      tool_name: null,
      images,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    setLocalMessages((prev) => [...prev, userMsg]);
    setMessage("");
    setStagedImages([]);
    setPendingRun({ conversationId, runId });

    const generate = (targetConversationId: number) => {
      generateMessageMutation.mutate(
        {
          conversationId: targetConversationId,
          data: {
            content,
            model_id: modelId,
            debug: debugMode,
            entity_ids: hasEntityTool ? selectedEntityIds : undefined,
            run_id: runId,
            images: images.length > 0 ? images : undefined,
          },
          optimisticMessage: {
            ...userMsg,
            conversation_id: targetConversationId,
          },
        },
        {
          onSuccess: (response) => {
            const isStillOnConversation =
              viewedConversationIdRef.current === targetConversationId;
            const isUserActivelyViewing =
              document.visibilityState === "visible" &&
              document.hasFocus() &&
              isStillOnConversation;

            if (
              "Notification" in window &&
              Notification.permission === "granted" &&
              !isUserActivelyViewing
            ) {
              const notification = new Notification("Como AI", {
                body: "Your response is ready.",
                icon: "/commo_logo.png",
                data: {
                  chatId: targetConversationId,
                },
              });
              notification.onclick = () => {
                window.focus();
                window.location.href = `/como-experts/${notification.data.chatId}`;
              };
            }
            if (isStillOnConversation) {
              setLocalMessages((prev) => [
                ...prev.filter((m) => m.id !== userMsg.id),
                response.user_message,
                response.assistant_message,
              ]);
            }
            if (response.debug && response.assistant_message?.id) {
              setDebugData((prev) => ({
                ...prev,
                [response.assistant_message.id]: response.debug!,
              }));
            }
            clearPendingRun();
          },
          onError: clearPendingRun,
        },
      );
    };

    if (conversationId) {
      generate(conversationId);
      return;
    }
    if (!selectedAgent) return;

    // Create the conversation first so it is listed, and can be reopened,
    // while the first reply is still being generated.
    createChatConversationMutation.mutate(selectedAgent.id, {
      onSuccess: (conversation) => {
        setPendingRun((prev) =>
          prev?.runId === runId
            ? { conversationId: conversation.id, runId }
            : prev,
        );
        if (viewedConversationIdRef.current === null) {
          navigate(`/como-experts/${conversation.id}`);
        }
        generate(conversation.id);
      },
      onError: clearPendingRun,
    });
  };

  const agentCount = agents.length;
  const agentGridItemProps =
    agentCount <= 1
      ? { xs: 12 as const, sm: 10 as const, md: 8 as const, lg: 6 as const }
      : agentCount === 2
        ? { xs: 6 as const, sm: 6 as const, md: 6 as const, lg: 6 as const }
        : agentCount <= 4
          ? { xs: 6 as const, sm: 6 as const, md: 6 as const, lg: 4 as const }
          : agentCount <= 8
            ? { xs: 6 as const, sm: 4 as const, md: 4 as const, lg: 4 as const }
            : {
                xs: 6 as const,
                sm: 4 as const,
                md: 3 as const,
                lg: 3 as const,
              };

  const agentBrowseView = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        px: { xs: 2, md: 5 },
        pt: { xs: 3, md: 5 },
        pb: { xs: 2, md: 4 },
      }}
    >
      <Typography
        variant="h5"
        sx={{ mb: 1, color: color.primary_dark, textAlign: "center" }}
      >
        {t("comoExperts.chooseAgent")}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          mb: 4,
          color: color.gray500,
          textAlign: "center",
          maxWidth: 680,
        }}
      >
        {t("comoExperts.chooseAgentSubtitle")}
      </Typography>
      <Grid
        container
        spacing={{ xs: 2, md: 2.5 }}
        justifyContent="center"
        sx={{ width: "100%", maxWidth: 1100 }}
      >
        {agents.map((agent) => {
          const slug = slugify(agent.name);
          const agentDescription = t(`comoExperts.agentDescriptions.${slug}`, {
            defaultValue: "",
          }).trim();
          return (
            <Grid
              item
              {...agentGridItemProps}
              key={agent.id}
              sx={{
                display: "flex",
                ...(agentCount === 1
                  ? {
                      maxWidth: { xs: "100%", sm: 480, md: 440 },
                      mx: "auto",
                      width: "100%",
                    }
                  : {}),
              }}
            >
              <Paper
                elevation={0}
                onClick={() => handleSelectAgent(agent)}
                sx={{
                  p: { xs: 2, md: 2.75 },
                  borderRadius: 3,
                  cursor: "pointer",
                  border: `2px solid ${color.gray200}`,
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  width: "100%",
                  minHeight: { xs: 148, md: agentDescription ? 178 : 162 },
                  transition: "all 0.18s ease-in-out",
                  bgcolor: color.white,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  "&:hover": {
                    borderColor: workspace.accent || color.accent,
                    boxShadow: "0 6px 20px rgba(242,141,1,0.12)",
                    bgcolor: "#fffaf4",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    flex: 1,
                    gap: { xs: 1, md: 1.25 },
                    pt: { xs: 0.25, md: 0.5 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 46, md: 52 },
                      height: { xs: 46, md: 52 },
                      borderRadius: "14px",
                      bgcolor: color.gray50,
                      border: `1.5px solid ${color.gray200}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: color.gray500,
                      flexShrink: 0,
                      transition: "all 0.18s ease-in-out",
                    }}
                  >
                    <AgentIcon
                      name={agent.name}
                      iconUrl={agent.icon}
                      size="md"
                    />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: workspace.primary_dark || color.primary_dark,
                      fontSize: { xs: "0.82rem", md: "0.9rem" },
                      lineHeight: 1.25,
                      maxWidth: "100%",
                      px: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      transition: "color 0.18s ease-in-out",
                    }}
                  >
                    {agent.name}
                  </Typography>
                  {agentDescription ? (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: color.gray600,
                        fontSize: { xs: "0.72rem", md: "0.74rem" },
                        lineHeight: 1.35,
                        px: 0.5,
                        maxWidth: "100%",
                        textAlign: "center",
                        flex: 1,
                      }}
                    >
                      {agentDescription}
                    </Typography>
                  ) : null}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const agentFocusedView =
    selectedAgent == null ? null : (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: { xs: 2, md: 4 },
          pt: { xs: 2, md: 3 },
          pb: { xs: 2, md: 3 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 560,
            mx: "auto",
            alignSelf: "stretch",
          }}
        >
          <Button
            startIcon={<ChevronLeftIcon />}
            onClick={handleClearAgentSelection}
            sx={{
              textTransform: "none",
              color: color.gray600,
              fontWeight: 600,
              fontSize: "0.85rem",
              mb: { xs: 2, md: 2.5 },
              px: 0.5,
              "&:hover": { bgcolor: `${color.gray100}` },
            }}
          >
            {t("comoExperts.changeTeamMember")}
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 520,
            mx: "auto",
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            border: `2px solid ${workspace.accent || color.accent}`,
            bgcolor: "#fffaf5",
            boxShadow: "0 8px 28px rgba(242,141,1,0.12)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: { xs: 1.5, md: 2 },
            }}
          >
            <Box
              sx={{
                width: { xs: 64, md: 72 },
                height: { xs: 64, md: 72 },
                borderRadius: "18px",
                bgcolor: "rgba(242,141,1,0.12)",
                border: `2px solid ${workspace.accent || color.accent}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: workspace.accent || color.accent,
                flexShrink: 0,
              }}
            >
              <AgentIcon
                name={selectedAgent.name}
                iconUrl={selectedAgent.icon}
                size="lg"
              />
            </Box>
            <Typography
              variant="h5"
              sx={{
                color: color.primary_dark,
                fontWeight: 700,
                fontSize: { xs: "1.15rem", md: "1.35rem" },
                lineHeight: 1.25,
                px: 1,
              }}
            >
              {selectedAgent.name}
            </Typography>
            {(() => {
              const slug = slugify(selectedAgent.name);
              const agentDescription = t(
                `comoExperts.agentDescriptions.${slug}`,
                { defaultValue: "" },
              ).trim();
              return agentDescription ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: color.gray600,
                    lineHeight: 1.5,
                    maxWidth: 440,
                    fontSize: "0.9rem",
                  }}
                >
                  {agentDescription}
                </Typography>
              ) : null;
            })()}
            <Typography
              variant="body2"
              sx={{
                color: color.gray500,
                fontSize: "0.85rem",
                maxWidth: 400,
                mt: 0.5,
              }}
            >
              {t("comoExperts.focusedChatSubtitle")}
            </Typography>
          </Box>
        </Paper>

        {selectedAgent.example_messages.length > 0 ? (
          <>
            <Typography
              variant="subtitle2"
              sx={{
                mt: { xs: 2.5, md: 3 },
                mb: 1.25,
                color: color.primary_dark,
                fontWeight: 700,
                letterSpacing: 0.02,
                textAlign: "center",
                width: "100%",
              }}
            >
              {t("comoExperts.suggestedStarters")}
            </Typography>
            <Box
              ref={examplePromptsRef}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
                maxWidth: 720,
                width: "100%",
                px: 0.5,
                scrollMarginBottom: {
                  xs: "calc(8rem + env(safe-area-inset-bottom, 0px))",
                  md: "7rem",
                },
                scrollMarginTop: "1rem",
              }}
            >
              {selectedAgent.example_messages.map((msg, idx) => (
                <Chip
                  key={idx}
                  label={msg}
                  onClick={() => setMessage(msg)}
                  clickable
                  sx={{
                    borderRadius: "999px",
                    border: `1px solid ${workspace.accent || color.accent}`,
                    color: color.primary_dark,
                    backgroundColor: color.white,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      whiteSpace: "normal",
                      textAlign: "left",
                      lineHeight: 1.35,
                      py: 0.5,
                      display: "block",
                    },
                    "&:hover": { backgroundColor: color.orange50 },
                  }}
                />
              ))}
            </Box>
          </>
        ) : null}
      </Box>
    );

  const activeAgent =
    selectedAgent || agents.find((a) => a.id === conversationDetail?.agent_id);

  const hasEntityTool = (activeAgent?.tools || []).includes(
    "retrieve_entity_data",
  );

  const hasDocumentReadTool = (activeAgent?.tools || []).includes(
    "document_read",
  );
  const canAttachDocuments = Boolean(
    hasDocumentReadTool && (selectedAgent || conversationId),
  );
  const isDocumentUploading =
    uploadDocumentMutation.isPending ||
    createEmptyConversationMutation.isPending;
  const shouldShowDocumentStrip =
    canAttachDocuments &&
    (isDocumentUploading || conversationDocuments.length > 0);

  const activeTools = (activeAgent?.tools || []).map((tid) => {
    const meta = allTools.find((t) => t.id === tid);
    return { id: tid, name: meta?.name || tid };
  });

  const activeKbNames = (activeAgent?.knowledge_base_ids || [])
    .map(
      (kid) => (allKnowledgeBases as any[]).find((kb) => kb.id === kid)?.name,
    )
    .filter(Boolean);

  const activeMcpToolNames: string[] = [];
  for (const mc of activeAgent?.mcp_connections || []) {
    for (const tool of mc.enabled_tools || []) {
      activeMcpToolNames.push(`${mc.name}: ${tool}`);
    }
  }

  const modelOptions = (llmData?.all_models || []).map(
    (model: {
      model_id: number;
      model_name: string;
      supports_images?: boolean;
    }) => ({
      id: model.model_id,
      label: model.model_name,
      iconSrc: getModelIcon(model.model_name),
      supportsImages: Boolean(model.supports_images),
    }),
  );

  const selectedModelSupportsImages = Boolean(
    modelOptions.find((o) => o.id === selectedModelId)?.supportsImages,
  );
  const canAttachImages = Boolean(
    selectedModelSupportsImages && (selectedAgent || conversationId),
  );

  useEffect(() => {
    if (!selectedModelSupportsImages && stagedImages.length > 0) {
      setStagedImages([]);
    }
  }, [selectedModelSupportsImages, stagedImages.length]);

  const totalEntityCount = accessibleEntities.length;
  const selectedEntityCount = selectedEntityIds.length;
  const areAllEntitiesSelected =
    totalEntityCount > 0 && selectedEntityCount === totalEntityCount;

  const entitySummaryText =
    selectedEntityCount === 0
      ? t("comoExperts.entities.summary.none")
      : areAllEntitiesSelected
        ? t("comoExperts.entities.summary.all", { count: totalEntityCount })
        : t("comoExperts.entities.summary.selected", {
            count: selectedEntityCount,
          });

  const handleSelectAllEntities = () => {
    setSelectedEntityIds(accessibleEntities.map((entity) => entity.id));
  };

  const handleUnselectAllEntities = () => {
    setSelectedEntityIds([]);
  };

  const totalToolsCount =
    activeTools.length + activeKbNames.length + activeMcpToolNames.length;
  const hasModelSelector = (llmData?.all_models?.length ?? 0) > 0;
  const hasEntitySelector = hasEntityTool && accessibleEntities.length > 0;

  const headerButtonSx = {
    height: { xs: 32, md: 36 },
    borderRadius: "10px",
    border: `1px solid ${color.gray200}`,
    bgcolor: color.white,
    color: color.primary_dark,
    px: 1.25,
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: { xs: "0.72rem", md: "0.78rem" },
    "&:hover": {
      bgcolor: color.gray50,
      borderColor: color.accent,
      color: color.accent,
    },
  };

  const headerToolChipElements = useMemo(() => {
    if (totalToolsCount === 0 || !activeAgent)
      return [] as React.ReactElement[];
    const compactSx = {
      flexShrink: 0,
      height: { xs: 16, md: 20 },
      fontSize: { xs: "0.55rem", md: "0.68rem" },
      px: { xs: 0.08, md: 0.25 },
      "& .MuiChip-icon": {
        fontSize: "10px !important",
        marginLeft: "4px",
      },
      "& .MuiChip-label": { px: { xs: 0.35, md: 0.75 } },
    };
    const items: React.ReactElement[] = [];
    for (const tool of activeTools) {
      const p = getToolPresentation(tool.id);
      const toolLabel = t(`chatWithExperts:tool.labels.${tool.id}`, {
        defaultValue: tool.name,
      });
      items.push(
        <Chip
          key={`ht-tool-${tool.id}`}
          icon={p.icon}
          label={toolLabel}
          size="small"
          sx={{
            flexShrink: 0,
            height: { xs: 16, md: 20 },
            fontSize: { xs: "0.55rem", md: "0.68rem" },
            px: { xs: 0.08, md: 0.25 },
            bgcolor: p.chip.bgcolor,
            color: p.chip.color,
            border: `1px solid ${p.chip.borderColor}`,
            "& .MuiChip-icon": {
              color: `${p.chip.iconColor} !important`,
              fontSize: "10px !important",
              marginLeft: "4px",
            },
            "& .MuiChip-label": { px: { xs: 0.35, md: 0.75 } },
          }}
        />,
      );
    }
    for (const name of activeKbNames) {
      items.push(
        <Chip
          key={`ht-kb-${name}`}
          icon={<MenuBookIcon sx={{ fontSize: "10px !important" }} />}
          label={name}
          size="small"
          sx={{
            ...compactSx,
            bgcolor: color.gray100,
            color: color.primary_dark,
            border: `1px solid ${color.gray200}`,
          }}
        />,
      );
    }
    for (const name of activeMcpToolNames) {
      items.push(
        <Chip
          key={`ht-mcp-${name}`}
          icon={<HubIcon sx={{ fontSize: "10px !important" }} />}
          label={name}
          size="small"
          sx={{
            ...compactSx,
            bgcolor: color.gray100,
            color: color.primary_dark,
            border: `1px solid ${color.gray200}`,
          }}
        />,
      );
    }
    return items;
  }, [
    totalToolsCount,
    activeAgent,
    activeTools,
    activeKbNames,
    activeMcpToolNames,
    t,
    color,
  ]);

  // The header names the first few capabilities; all of them are one click
  // away, so a long tool list never wraps the header.
  const HEADER_CAPABILITIES_VISIBLE = 3;
  const capabilityLabels = activeAgent
    ? [
        ...activeTools.map((tool) =>
          t(`chatWithExperts:tool.labels.${tool.id}`, {
            defaultValue: tool.name,
          }),
        ),
        ...activeKbNames,
        ...activeMcpToolNames,
      ]
    : [];
  const hiddenCapabilityCount =
    capabilityLabels.length - HEADER_CAPABILITIES_VISIBLE;

  const capabilitiesSummary =
    capabilityLabels.length === 0 ? null : (
      <>
        <ButtonBase
          onClick={(e) => setToolMoreAnchor(e.currentTarget)}
          aria-haspopup="dialog"
          aria-label={t("comoExperts.tools.allCapabilities")}
          sx={{
            maxWidth: "100%",
            mt: 0.25,
            ml: -0.75,
            px: 0.75,
            py: 0.25,
            gap: 0.75,
            borderRadius: "6px",
            justifyContent: "flex-start",
            color: color.gray500,
            "&:hover": { bgcolor: color.gray100, color: color.primary_dark },
          }}
        >
          <BuildOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
          <Box
            component="span"
            sx={{
              minWidth: 0,
              fontSize: 12.5,
              lineHeight: "18px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {capabilityLabels.slice(0, HEADER_CAPABILITIES_VISIBLE).join(" · ")}
          </Box>
          {hiddenCapabilityCount > 0 && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: 0.75,
                borderRadius: "8px",
                fontSize: 11.5,
                fontWeight: 700,
                lineHeight: "18px",
                bgcolor: color.gray100,
                color: color.gray700,
              }}
            >
              +{hiddenCapabilityCount}
            </Box>
          )}
          <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        </ButtonBase>
        <Popover
          open={Boolean(toolMoreAnchor)}
          anchorEl={toolMoreAnchor}
          onClose={() => setToolMoreAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              border: `1px solid ${color.gray200}`,
              boxShadow: "0 8px 24px rgba(16, 24, 40, 0.12)",
              maxHeight: "min(70vh, 380px)",
              overflowY: "auto",
            },
          }}
        >
          <Box
            sx={{
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              minWidth: { xs: 260, sm: 300 },
              maxWidth: 400,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: color.gray600, fontWeight: 700, px: 0.25 }}
            >
              {t("comoExperts.tools.allCapabilities")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {headerToolChipElements}
            </Box>
          </Box>
        </Popover>
      </>
    );

  const chatHeader = (
    <Box
      sx={{ bgcolor: color.white, borderBottom: `1px solid ${color.gray100}` }}
    >
      <Box
        sx={{
          px: { xs: 1.25, md: 2.5 },
          py: { xs: 1, md: 1.5 },
          display: "flex",
          flexWrap: { xs: "wrap", md: "nowrap" },
          alignItems: "center",
          gap: { xs: 1, md: 1.5 },
          rowGap: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: 34, md: 40 },
            height: { xs: 34, md: 40 },
            borderRadius: "12px",
            bgcolor: workspace.primary_dark || color.primary_dark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <SupportAgentRoundedIcon
            sx={{ fontSize: { xs: 19, md: 22 }, color: color.white }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            noWrap
            sx={{
              maxWidth: "100%",
              color: color.primary_dark,
              fontSize: { xs: 15, md: 16 },
              fontWeight: 700,
              lineHeight: "22px",
            }}
          >
            {activeAgent?.name ||
              conversationDetail?.agent_name ||
              t("comoExperts.defaultAgentName")}
          </Typography>
          {capabilitiesSummary}
        </Box>

        {/* Right: model + entities — on phone full-width row, equal flex so labels stay readable */}
        {activeAgent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, md: 0.75 },
              flexShrink: 0,
              flexBasis: { xs: "100%", md: "auto" },
              minWidth: 0,
              width: { xs: "100%", md: "auto" },
            }}
          >
            <Box
              sx={{
                flex: { xs: 1, md: 0 },
                flexBasis: { xs: 0, md: "auto" },
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, md: 0.75 },
                overflow: "hidden",
              }}
            >
              {/* Model button + popover */}
              {hasModelSelector &&
                (() => {
                  const selModel = modelOptions.find(
                    (o) => o.id === (selectedModelId ?? ""),
                  );
                  const shortLabel = selModel
                    ? selModel.label.split("/").pop() || selModel.label
                    : t("comoExperts.model.placeholder");
                  return (
                    <>
                      <Tooltip
                        title={
                          selModel?.label || t("comoExperts.model.placeholder")
                        }
                      >
                        <Button
                          size="small"
                          onClick={(e) => setModelAnchor(e.currentTarget)}
                          endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                          aria-label={selModel?.label || shortLabel}
                          sx={{
                            ...headerButtonSx,
                            justifyContent: "flex-start",
                            "& .MuiButton-endIcon": { ml: 0.25, flexShrink: 0 },
                            flex: isMobile ? 1 : undefined,
                            minWidth: 0,
                            px: { xs: 0.65, md: 1.25 },
                            maxWidth: isMobile ? "none" : undefined,
                          }}
                        >
                          {selModel?.iconSrc && (
                            <Box
                              component="img"
                              src={selModel.iconSrc}
                              alt=""
                              sx={{
                                width: 16,
                                height: 16,
                                objectFit: "contain",
                                mr: 0.5,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Box
                            component="span"
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              textAlign: "left",
                              maxWidth: isMobile ? "none" : 140,
                            }}
                          >
                            {shortLabel}
                          </Box>
                        </Button>
                      </Tooltip>

                      <Popover
                        open={Boolean(modelAnchor)}
                        anchorEl={modelAnchor}
                        onClose={() => setModelAnchor(null)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        PaperProps={{
                          sx: {
                            mt: 0.75,
                            minWidth: 280,
                            maxWidth: 360,
                            maxHeight: 320,
                            overflowY: "auto",
                            borderRadius: 2,
                            border: `1px solid ${color.gray200}`,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                            p: 0.5,
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: color.gray500,
                            fontWeight: 600,
                            px: 1.5,
                            pt: 1,
                            pb: 0.5,
                          }}
                        >
                          {t("comoExperts.model.label")}
                        </Typography>
                        {modelOptions.map((m) => {
                          const isCurrent = m.id === (selectedModelId ?? "");
                          return (
                            <Box
                              key={m.id}
                              onClick={() => {
                                setSelectedModelId(Number(m.id));
                                setModelAnchor(null);
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                cursor: "pointer",
                                bgcolor: isCurrent
                                  ? `${color.primary_dark}0d`
                                  : "transparent",
                                borderLeft: `3px solid ${isCurrent ? color.accent : "transparent"}`,
                                transition: "all 0.13s",
                                "&:hover": {
                                  bgcolor: `${color.primary_dark}0a`,
                                },
                              }}
                            >
                              {m.iconSrc ? (
                                <Box
                                  component="img"
                                  src={m.iconSrc}
                                  alt=""
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    objectFit: "contain",
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: "6px",
                                    bgcolor: color.gray100,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: color.primary_dark,
                                  fontWeight: isCurrent ? 600 : 400,
                                  fontSize: "0.83rem",
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {m.label}
                              </Typography>
                              {isCurrent && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    bgcolor: color.accent,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Popover>
                    </>
                  );
                })()}

              {/* Entities dropdown — same control on mobile & desktop; flex 1 on phone next to model */}
              {hasEntitySelector && (
                <Tooltip
                  title={`${t("comoExperts.entities.label")}: ${entitySummaryText}`}
                >
                  <Button
                    size="small"
                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                    startIcon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      ...headerButtonSx,
                      justifyContent: "flex-start",
                      "& .MuiButton-endIcon": { ml: 0.25, flexShrink: 0 },
                      "& .MuiButton-startIcon": { mr: 0.35, flexShrink: 0 },
                      flex: isMobile ? 1 : undefined,
                      minWidth: 0,
                      px: { xs: 0.65, md: 1.25 },
                    }}
                    aria-label={t("comoExperts.entities.label")}
                  >
                    <Box
                      component="span"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "left",
                        maxWidth: isMobile ? "none" : 100,
                      }}
                    >
                      {entitySummaryText}
                    </Box>
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Tooltip
              title={
                debugMode
                  ? t("comoExperts.debug.disable")
                  : t("comoExperts.debug.enable")
              }
            >
              <IconButton
                size="small"
                onClick={() => setDebugMode((d) => !d)}
                aria-pressed={debugMode}
                aria-label={
                  debugMode
                    ? t("comoExperts.debug.disable")
                    : t("comoExperts.debug.enable")
                }
                sx={{
                  flexShrink: 0,
                  height: { xs: 32, md: 36 },
                  width: { xs: 32, md: 36 },
                  borderRadius: "10px",
                  border: `1px solid ${debugMode ? color.accent : color.gray200}`,
                  bgcolor: debugMode ? color.orange50 : color.white,
                  color: debugMode ? color.accent : color.gray600,
                  "&:hover": {
                    bgcolor: debugMode ? color.orange50 : color.gray50,
                    borderColor: color.accent,
                    color: color.accent,
                  },
                }}
              >
                <PsychologyOutlinedIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Entities popover */}
      <Popover
        open={Boolean(settingsAnchor)}
        anchorEl={settingsAnchor}
        onClose={() => setSettingsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: { xs: "calc(100vw - 32px)", sm: 320 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 0.5,
            px: 1.5,
            pt: 1.25,
            pb: 0.5,
            borderBottom: `1px solid ${color.gray100}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: color.primary_dark,
              fontWeight: 700,
              flex: 1,
              whiteSpace: "nowrap",
            }}
          >
            {t("comoExperts.entities.label")}
          </Typography>
          <Button
            size="small"
            variant="text"
            onClick={handleSelectAllEntities}
            disabled={areAllEntitiesSelected}
            sx={{
              minWidth: "auto",
              px: 0.75,
              py: 0.25,
              fontSize: "0.7rem",
              textTransform: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              color: color.primary_dark,
              "&:hover": { color: color.accent },
            }}
          >
            {t("comoExperts.entities.selectAll")}
          </Button>
          <Button
            size="small"
            variant="text"
            onClick={handleUnselectAllEntities}
            disabled={selectedEntityCount === 0}
            sx={{
              minWidth: "auto",
              px: 0.75,
              py: 0.25,
              fontSize: "0.7rem",
              textTransform: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              color: color.gray500,
              "&:hover": { color: color.accent },
            }}
          >
            {t("comoExperts.entities.unselect")}
          </Button>
        </Box>

        {/* Entity list with checkboxes */}
        <Box sx={{ maxHeight: 260, overflowY: "auto", py: 0.5 }}>
          {accessibleEntities.length === 0 && (
            <Typography
              variant="caption"
              sx={{ display: "block", px: 1.5, py: 1, color: color.gray500 }}
            >
              {t("comoExperts.noEntities")}
            </Typography>
          )}
          {accessibleEntities.map((entity) => {
            const checked = selectedEntityIds.includes(entity.id);
            return (
              <Box
                key={entity.id}
                onClick={() => {
                  setSelectedEntityIds((prev) =>
                    prev.includes(entity.id)
                      ? prev.filter((id) => id !== entity.id)
                      : [...prev, entity.id],
                  );
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  mx: 0.5,
                  cursor: "pointer",
                  bgcolor: checked ? `${color.primary_dark}0d` : "transparent",
                  borderLeft: `3px solid ${checked ? color.accent : "transparent"}`,
                  transition: "all 0.13s",
                  "&:hover": { bgcolor: `${color.primary_dark}0a` },
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  disableRipple
                  sx={{
                    p: 0.25,
                    color: color.gray300,
                    "&.Mui-checked": { color: color.accent },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.83rem",
                    color: color.primary_dark,
                    fontWeight: checked ? 600 : 400,
                  }}
                >
                  {entity.name}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Popover>
    </Box>
  );

  const chatMessages = (
    <Box
      ref={messagesScrollRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        p: { xs: 1, md: 2 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isConversationLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <CircularProgress sx={{ color: color.gray500 }} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: 1080 },
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, md: 2.5 },
              py: { xs: 0.5, md: 1 },
            }}
          >
            {hasMoreMessages && <Box ref={loadMoreRef} sx={{ height: 1 }} />}
            {localMessages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((msg) => {
                const showToggle =
                  msg.role === "user" && (msg.content?.length ?? 0) > 200;

                return msg.is_scheduled ? (
                  <Box
                    key={msg.id}
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      py: 0.75,
                      px: { xs: 0.75, md: 1.5 },
                      boxSizing: "border-box",
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "999px",
                        bgcolor: color.gray100,
                        border: `1px solid ${color.gray200}`,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: color.gray600,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      >
                        ⏱ Scheduled
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    key={msg.id}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      maxWidth: { xs: "100%", md: "85%" },
                      minWidth: 0,
                      pl: msg.role === "assistant" ? 0 : { md: 4 },
                      pr: msg.role === "user" ? 0 : { md: 4 },
                    }}
                  >
                    {msg.role === "assistant" && (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: color.gray100,
                          border: `1px solid ${color.gray200}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: color.gray700,
                          flexShrink: 0,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          mt: 0.25,
                        }}
                      >
                        <AgentIcon
                          name={
                            activeAgent?.name ||
                            conversationDetail?.agent_name ||
                            "assistant"
                          }
                          iconUrl={activeAgent?.icon}
                          size="sm"
                        />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems:
                          msg.role === "user" ? "flex-end" : "flex-start",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          minWidth: 0,
                          maxWidth: { xs: "calc(100% - 40px)", md: "100%" },
                          px: { xs: 1.5, md: 2.25 },
                          py: { xs: 1.1, md: 1.5 },
                          borderRadius:
                            msg.role === "assistant"
                              ? "6px 20px 20px 20px"
                              : "20px 6px 20px 20px",
                          bgcolor:
                            msg.role === "user"
                              ? color.primary_dark
                              : color.gray50,
                          color:
                            msg.role === "user"
                              ? color.white
                              : color.textPrimary,
                          border: `1px solid ${
                            msg.role === "user"
                              ? "rgba(255,255,255,0.06)"
                              : color.gray200
                          }`,
                          boxShadow:
                            msg.role === "user"
                              ? "0 2px 8px rgba(0,0,0,0.12)"
                              : "0 1px 3px rgba(0,0,0,0.06)",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {msg.role === "assistant" ? (
                          <ChatMarkdown
                            content={msg.content}
                            conversationId={conversationId || undefined}
                          />
                        ) : (
                          <Box
                            sx={{
                              position: "relative",
                              maxHeight: expandedMessages[msg.id] ? 500 : 120,
                              overflow: "hidden",
                              cursor: "pointer",
                              transition: "max-height 0.4s ease",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                                fontSize: "0.9rem",
                                color: color.white,
                                fontWeight: 500,
                                margin: "0.5rem 0",
                              }}
                            >
                              {msg.content}
                            </Typography>
                            {showToggle && !expandedMessages[msg.id] && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: 75,
                                  pointerEvents: "none",
                                  background: `linear-gradient(to bottom, transparent, ${color.primary_dark})`,
                                }}
                              />
                            )}
                            {showToggle && (
                              <Tooltip
                                title={
                                  expandedMessages[msg.id]
                                    ? t("comoExperts.collapseText")
                                    : t("comoExperts.expandText")
                                }
                                arrow
                                placement="bottom"
                                open={tooltipOpen}
                                onOpen={() => setTooltipOpen(true)}
                                onClose={() => setTooltipOpen(false)}
                              >
                                <Box
                                  onClick={() => {
                                    toggleMessage(msg.id);
                                    setTooltipOpen(false);
                                  }}
                                  sx={{
                                    position: "absolute",
                                    bottom: 1,
                                    right: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: color.white,
                                    cursor: "pointer",
                                    userSelect: "none",
                                    padding: "4px",
                                    "&:hover": {
                                      transform: "scale(1.07)",
                                    },
                                  }}
                                >
                                  {expandedMessages[msg.id] ? (
                                    <ExpandLessIcon fontSize="medium" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="medium" />
                                  )}
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                        {msg.images && msg.images.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                              mt: msg.content ? 1 : 0,
                              justifyContent:
                                msg.role === "user" ? "flex-end" : "flex-start",
                            }}
                          >
                            {msg.images.map((src, idx) => (
                              <Box
                                key={`msg-${msg.id}-img-${idx}`}
                                component="img"
                                src={src}
                                alt=""
                                onClick={() => setPreviewImage(src)}
                                sx={{
                                  maxWidth: 220,
                                  maxHeight: 220,
                                  borderRadius: "10px",
                                  cursor: "pointer",
                                  border: `1px solid ${color.gray200}`,
                                  objectFit: "cover",
                                }}
                              />
                            ))}
                          </Box>
                        )}

                        {msg.role === "assistant" &&
                          Boolean(msg.references?.length) && (
                            <Box sx={{ mt: 1, minWidth: 0, maxWidth: "100%" }}>
                              <Box
                                component="button"
                                type="button"
                                onClick={() =>
                                  setReferencesExpanded((prev) => ({
                                    ...prev,
                                    [msg.id]: !prev[msg.id],
                                  }))
                                }
                                aria-expanded={Boolean(
                                  referencesExpanded[msg.id],
                                )}
                                sx={{
                                  width: "100%",
                                  minWidth: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  py: 0.75,
                                  px: 1,
                                  borderRadius: "10px",
                                  border: `1px solid ${color.gray200}`,
                                  bgcolor: color.gray50,
                                  cursor: "pointer",
                                  textAlign: "left",
                                  font: "inherit",
                                  color: color.primary_dark,
                                  boxSizing: "border-box",
                                  "&:hover": {
                                    borderColor: color.accent,
                                    bgcolor: color.white,
                                  },
                                }}
                              >
                                <SourceOutlinedIcon
                                  sx={{
                                    fontSize: 18,
                                    color: color.accent,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontWeight: 600,
                                    color: color.gray700,
                                    lineHeight: 1.3,
                                  }}
                                >
                                  {t("comoExperts.references.title", {
                                    count: msg.references?.length ?? 0,
                                  })}
                                </Typography>
                                {referencesExpanded[msg.id] ? (
                                  <KeyboardArrowUpIcon
                                    sx={{
                                      fontSize: 20,
                                      color: color.gray600,
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : (
                                  <KeyboardArrowDownIcon
                                    sx={{
                                      fontSize: 20,
                                      color: color.gray600,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </Box>
                              <Collapse
                                in={Boolean(referencesExpanded[msg.id])}
                              >
                                <Box sx={{ pt: 1, minWidth: 0 }}>
                                  {(msg.references ?? []).map(
                                    (reference, index) => (
                                      <Box
                                        key={`${msg.id}-reference-${index}`}
                                        sx={{
                                          mb: 1,
                                          p: 1.25,
                                          borderRadius: "10px",
                                          border: `1px solid ${color.gray200}`,
                                          bgcolor: color.white,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            minWidth: 0,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              fontWeight: 700,
                                              color: color.primary_dark,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {reference.source}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={
                                              reference.kind === "entity"
                                                ? t(
                                                    "comoExperts.references.entity",
                                                  )
                                                : t(
                                                    "comoExperts.references.knowledgeBase",
                                                  )
                                            }
                                            sx={{
                                              height: 18,
                                              fontSize: "0.6rem",
                                              bgcolor: color.gray100,
                                              color: color.gray600,
                                            }}
                                          />
                                        </Box>

                                        <Typography
                                          variant="caption"
                                          sx={{
                                            display: "block",
                                            color: color.gray500,
                                            mt: 0.25,
                                            fontSize: "0.68rem",
                                          }}
                                        >
                                          {t("comoExperts.references.query")}:{" "}
                                          {reference.query}
                                        </Typography>

                                        <Box
                                          component="ul"
                                          sx={{ m: 0, mt: 0.75, pl: 2.5 }}
                                        >
                                          {reference.documents.map(
                                            (document, documentIndex) => (
                                              <Typography
                                                component="li"
                                                variant="caption"
                                                key={`${msg.id}-reference-${index}-${documentIndex}`}
                                                sx={{
                                                  color: color.gray700,
                                                  fontSize: "0.72rem",
                                                  wordBreak: "break-word",
                                                }}
                                              >
                                                {document}
                                              </Typography>
                                            ),
                                          )}
                                        </Box>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </Collapse>
                            </Box>
                          )}

                        {msg.role === "assistant" && debugData[msg.id] && (
                          <Box sx={{ mt: 1, minWidth: 0, maxWidth: "100%" }}>
                            <Box
                              component="button"
                              type="button"
                              onClick={() =>
                                setReasoningExpanded((prev) => ({
                                  ...prev,
                                  [msg.id]: !prev[msg.id],
                                }))
                              }
                              aria-expanded={Boolean(reasoningExpanded[msg.id])}
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                py: 0.75,
                                px: 1,
                                borderRadius: "10px",
                                border: `1px solid ${color.gray200}`,
                                bgcolor: color.orange50,
                                cursor: "pointer",
                                textAlign: "left",
                                font: "inherit",
                                color: color.primary_dark,
                                boxSizing: "border-box",
                                "&:hover": {
                                  borderColor: color.accent,
                                  bgcolor: color.white,
                                },
                              }}
                            >
                              <PsychologyOutlinedIcon
                                sx={{
                                  fontSize: 18,
                                  color: color.accent,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontWeight: 600,
                                  color: color.gray700,
                                  lineHeight: 1.3,
                                }}
                              >
                                {t("comoExperts.debug.steps", {
                                  count: debugData[msg.id].steps.length,
                                })}
                              </Typography>
                              {reasoningExpanded[msg.id] ? (
                                <KeyboardArrowUpIcon
                                  sx={{
                                    fontSize: 20,
                                    color: color.gray600,
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    fontSize: 20,
                                    color: color.gray600,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                            <Collapse in={Boolean(reasoningExpanded[msg.id])}>
                              <Box sx={{ pt: 1, minWidth: 0 }}>
                                {debugData[msg.id].note && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: color.gray500,
                                      display: "block",
                                      mb: 1,
                                    }}
                                  >
                                    {debugData[msg.id].note}
                                  </Typography>
                                )}
                                {debugData[msg.id].steps.map((step, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      mb: 1,
                                      p: 1,
                                      borderRadius: "8px",
                                      bgcolor: color.gray50,
                                      border: `1px solid ${color.gray200}`,
                                      minWidth: 0,
                                    }}
                                  >
                                    {step.thought && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: "block",
                                          color: color.gray600,
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {step.thought}
                                      </Typography>
                                    )}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.5,
                                        minWidth: 0,
                                      }}
                                    >
                                      <BuildIcon
                                        sx={{
                                          fontSize: 12,
                                          color: color.gray600,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                          color: color.primary_dark,
                                          minWidth: 0,
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {step.tool}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: color.gray600,
                                        ml: { xs: 0, sm: 2 },
                                        mt: 0.5,
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {t("comoExperts.debug.input")}:{" "}
                                      {typeof step.tool_input === "string"
                                        ? step.tool_input
                                        : JSON.stringify(step.tool_input)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: color.gray500,
                                        ml: { xs: 0, sm: 2 },
                                        mt: 0.5,
                                        maxHeight: 120,
                                        overflow: "auto",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                      }}
                                    >
                                      {t("comoExperts.debug.result")}:{" "}
                                      {step.observation}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Collapse>
                          </Box>
                        )}
                      </Box>
                      <Tooltip
                        title={formatFullDate(msg.created_at)}
                        arrow
                        placement="bottom"
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: color.gray400 || color.gray500,
                            userSelect: "none",
                            cursor: "default",
                            alignSelf:
                              msg.role === "user" ? "flex-end" : "flex-start",
                            mr: msg.role === "user" ? 1 : 0,
                            ml: msg.role === "assistant" ? 1 : 0,
                          }}
                        >
                          {formatRelativeTime(msg.created_at)}
                        </Typography>
                      </Tooltip>

                      {msg.role === "user" && (
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            {isAgentBusy && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-start",
                  px: { xs: 0.75, md: 1.5 },
                  boxSizing: "border-box",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    maxWidth: { xs: "100%", md: "85%" },
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: color.gray100,
                      border: `1px solid ${color.gray200}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: color.gray700,
                      flexShrink: 0,
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      mt: 0.25,
                    }}
                  >
                    <AgentIcon
                      name={
                        activeAgent?.name ||
                        conversationDetail?.agent_name ||
                        "assistant"
                      }
                      iconUrl={activeAgent?.icon}
                      size="sm"
                    />
                  </Box>

                  <Box
                    sx={{
                      minWidth: 0,
                      maxWidth: { xs: "calc(100% - 40px)", md: "100%" },
                      px: { xs: 1.5, md: 2.25 },
                      py: { xs: 1.1, md: 1.5 },
                      borderRadius: "6px 20px 20px 20px",
                      bgcolor: color.gray50,
                      border: `1px solid ${color.gray200}`,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                  >
                    {latestStep ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress
                          size={12}
                          sx={{ color: color.gray500 }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: color.gray600, fontStyle: "italic" }}
                        >
                          {latestStep}
                        </Typography>
                      </Box>
                    ) : (
                      <img
                        src="/typing.gif"
                        alt={t("comoExperts.typingAlt")}
                        style={{ height: "1rem", width: "auto" }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <div ref={messagesEndRef} />
        </>
      )}
    </Box>
  );

  const documentStrip = shouldShowDocumentStrip ? (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.75,
        alignItems: "center",
      }}
    >
      {isDocumentUploading && (
        <Typography variant="caption" sx={{ color: color.gray400 }}>
          {t("comoExperts.documents.uploading")}
        </Typography>
      )}
      {conversationDocuments.map((doc) => {
        const { Icon: DocTypeIcon, iconColor } = getFileTypeStyle(doc.filename);
        return (
          <Chip
            key={`doc-${doc.id}`}
            icon={<DocTypeIcon sx={{ fontSize: 14, color: iconColor }} />}
            label={doc.filename}
            size="small"
            variant="outlined"
            onDelete={() => handleRemoveDocument(doc.id)}
            deleteIcon={
              <DeleteIcon
                sx={{ fontSize: 14 }}
                aria-label={t("comoExperts.documents.remove")}
              />
            }
            sx={{
              color: color.primary_dark,
              borderColor: workspace.accent || color.accent,
              "& .MuiChip-icon": { color: iconColor },
            }}
          />
        );
      })}
    </Box>
  ) : null;

  const chatInput = (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        borderTop: `1px solid ${color.gray100}`,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{ width: "100%", maxWidth: { xs: "100%", md: 1080 }, mx: "auto" }}
      >
        {canAttachDocuments && (
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
            accept=".pdf,.docx,.doc,.ppt,.pptx,.xlsx,.xls,.csv,.txt"
          />
        )}
        {canAttachImages && (
          <input
            ref={imageInputRef}
            type="file"
            hidden
            multiple
            onChange={handleImageChange}
            accept="image/png,image/jpeg,image/webp,image/gif"
          />
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {canAttachDocuments && (
            <Tooltip
              title={
                isLibraryOverLimit
                  ? t("comoExperts.libraryLimitReachedShort")
                  : t("comoExperts.documents.attach")
              }
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                invisible={!isLibraryOverLimit}
                badgeContent={
                  <PriorityHighIcon sx={{ fontSize: 11, color: color.white }} />
                }
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: "#ed6c02",
                    minWidth: 16,
                    height: 16,
                    borderRadius: "50%",
                    p: 0,
                  },
                }}
              >
                <IconButton
                  onClick={handleAttachClick}
                  disabled={isDocumentUploading || isLibraryOverLimit}
                  size="small"
                  sx={{
                    color: color.primary_dark,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "10px",
                    bgcolor: color.white,
                    "&:hover": {
                      bgcolor: color.gray50,
                      borderColor: color.accent,
                      color: color.accent,
                    },
                    ...(isLibraryOverLimit && {
                      opacity: 0.45,
                      filter: "blur(0.6px)",
                      "&.Mui-disabled": { color: color.primary_dark },
                    }),
                  }}
                >
                  <AttachFileIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>
          )}
          {canAttachImages && (
            <Tooltip
              title={
                isLibraryOverLimit
                  ? t("comoExperts.libraryLimitReachedShort")
                  : t("comoExperts.images.attach", {
                      defaultValue: "Attach image",
                    })
              }
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                invisible={!isLibraryOverLimit}
                badgeContent={
                  <PriorityHighIcon sx={{ fontSize: 11, color: color.white }} />
                }
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: "#ed6c02",
                    minWidth: 16,
                    height: 16,
                    borderRadius: "50%",
                    p: 0,
                  },
                }}
              >
                <IconButton
                  onClick={handleImageAttachClick}
                  disabled={isLibraryOverLimit}
                  size="small"
                  sx={{
                    color: color.primary_dark,
                    border: `1px solid ${color.gray200}`,
                    borderRadius: "10px",
                    bgcolor: color.white,
                    "&:hover": {
                      bgcolor: color.gray50,
                      borderColor: color.accent,
                      color: color.accent,
                    },
                    ...(isLibraryOverLimit && {
                      opacity: 0.45,
                      filter: "blur(0.6px)",
                      "&.Mui-disabled": { color: color.primary_dark },
                    }),
                  }}
                >
                  <ImageOutlinedIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>
          )}

          <Box sx={{ position: "relative", flexGrow: 1, minWidth: 0 }}>
            <ComoInput
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              inputRef={messageInputRef}
              autoFocus
              placeholder={
                selectedAgent
                  ? t("comoExperts.placeholder.messageAgent", {
                      agentName: selectedAgent.name,
                    })
                  : t("comoExperts.placeholder.selectAgent")
              }
              disabled={(!selectedAgent && !conversationId) || isAgentBusy}
              multiline
              maxRows={4}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              InputProps={{
                endAdornment: audioRecorder.isSupported ? (
                  <InputAdornment
                    position="end"
                    sx={{ alignSelf: "center", height: "auto", ml: 0 }}
                  >
                    <Tooltip
                      title={
                        audioRecorder.isRecording
                          ? t("comoExperts.voice.stop")
                          : t("comoExperts.voice.record")
                      }
                    >
                      <span style={{ display: "inline-flex" }}>
                        <IconButton
                          onClick={handleVoiceClick}
                          disabled={
                            (!selectedAgent && !conversationId) ||
                            isAgentBusy ||
                            transcribeAudioMutation.isPending
                          }
                          size="small"
                          sx={{
                            p: "6px",
                            color: audioRecorder.isRecording
                              ? color.accent
                              : color.gray500,
                            "&:hover": { color: color.accent },
                          }}
                        >
                          {transcribeAudioMutation.isPending ? (
                            <CircularProgress
                              size={18}
                              sx={{ color: color.gray400 }}
                            />
                          ) : audioRecorder.isRecording ? (
                            <StopIcon fontSize="small" />
                          ) : (
                            <MicNoneIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            {audioRecorder.isRecording && (
              <Box
                sx={{
                  position: "absolute",
                  top: 2,
                  bottom: 2,
                  left: 6,
                  right: 48,
                  pl: "42px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  bgcolor: color.white,
                  borderRadius: 2,
                  pointerEvents: "none",
                  "@keyframes voiceWave": {
                    "0%, 100%": { transform: "scaleY(0.35)" },
                    "50%": { transform: "scaleY(1)" },
                  },
                }}
              >
                {VOICE_WAVE_BARS.map((height, index) => (
                  <Box
                    key={`voice-wave-${index}`}
                    sx={{
                      width: "4px",
                      height,
                      flexShrink: 0,
                      borderRadius: "3px",
                      bgcolor: color.accent,
                      transformOrigin: "center",
                      animation:
                        "voiceWave 1.6s cubic-bezier(0.45, 0, 0.55, 1) infinite",
                      animationDelay: `${index * 0.06}s`,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleSendMessage}
            disabled={
              (!message.trim() && stagedImages.length === 0) ||
              (!selectedAgent && !conversationId) ||
              isAgentBusy
            }
            sx={{
              bgcolor: color.primary_dark,
              color: color.white,
              "&:hover": {
                bgcolor: color.accent,
              },
              "&:disabled": {
                bgcolor: color.gray300,
                color: color.white,
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>

        {shouldShowDocumentStrip && (
          <Box sx={{ mt: 0.75 }}>{documentStrip}</Box>
        )}

        {stagedImages.length > 0 && (
          <Box sx={{ mt: 0.75, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {stagedImages.map((img, index) => (
              <Box
                key={`staged-img-${index}`}
                sx={{
                  position: "relative",
                  width: 56,
                  height: 56,
                  borderRadius: "10px",
                  overflow: "hidden",
                  border: `1px solid ${color.gray200}`,
                }}
              >
                <Box
                  component="img"
                  src={img.dataUrl}
                  alt={img.name}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveStagedImage(index)}
                  title={t("comoExperts.images.remove", {
                    defaultValue: "Remove image",
                  })}
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    p: 0.15,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: color.white,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        // Full height of the page area; phones keep the 56px top bar above it.
        height: { xs: "calc(100vh - 56px)", sm: "100vh" },
        // Next to the floating sidebar the chat floats too, with the same gaps.
        p: { xs: 0, sm: 1.5 },
      }}
    >
      {sidebarSlot &&
        createPortal(
          <ComoExpertsSidebarNav
            activeConversationId={conversationId}
            onNewChat={handleNewChat}
            onSelectConversation={handleSelectConversation}
            onOpenLibrary={() => setLibraryOpen(true)}
            onOpenAgentMemory={() => setAgentMemoryOpen(true)}
            onOpenScheduledTasks={() => setScheduledTasksOpen(true)}
          />,
          sidebarSlot,
        )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          bgcolor: color.white,
          border: { xs: "none", sm: `1px solid ${color.gray200}` },
          borderRadius: { xs: 0, sm: "16px" },
          overflow: "hidden",
        }}
      >
        {(activeAgent || conversationId) && chatHeader}

        {conversationId || localMessages.length > 0
          ? chatMessages
          : selectedAgent
            ? agentFocusedView
            : agentBrowseView}

        {(selectedAgent || conversationId) && chatInput}
      </Box>

      <Modal
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.8)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            outline: "none",
            maxWidth: "95vw",
            maxHeight: "95vh",
            display: "flex",
          }}
          onClick={() => setPreviewImage(null)}
        >
          <IconButton
            onClick={() => setPreviewImage(null)}
            aria-label={t("comoExperts.images.closePreview", {
              defaultValue: "Close preview",
            })}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(0,0,0,0.55)",
              color: color.white,
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt=""
              onClick={(e) => e.stopPropagation()}
              sx={{
                maxWidth: "95vw",
                maxHeight: "95vh",
                objectFit: "contain",
                borderRadius: "8px",
                boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
              }}
            />
          )}
        </Box>
      </Modal>

      <Modal
        open={agentMemoryOpen}
        onClose={() => setAgentMemoryOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.4)",
          p: { xs: 1.5, md: 4 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            outline: "none",
            width: "100%",
            maxWidth: 980,
            height: "88vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: color.white,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <UserMemory onClose={() => setAgentMemoryOpen(false)} />
          </Box>
        </Box>
      </Modal>
      <Modal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.4)",
          p: { xs: 1.5, md: 4 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            outline: "none",
            width: "100%",
            maxWidth: 980,
            height: "88vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: color.white,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <LibraryView
              onClose={() => setLibraryOpen(false)}
              onOpenConversation={(id) => {
                setLibraryOpen(false);
                navigate(`/como-experts/${id}`);
              }}
            />
          </Box>
        </Box>
      </Modal>
      <Modal
        open={scheduledTasksOpen}
        onClose={() => setScheduledTasksOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.4)",
          p: { xs: 1.5, md: 4 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            outline: "none",
            width: "100%",
            maxWidth: 980,
            height: "88vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: color.white,
            borderRadius: "14px",
            border: `1px solid ${color.gray200}`,
            boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <MyScheduledTasks
              onClose={() => setScheduledTasksOpen(false)}
              onOpenConversation={(id) => {
                setScheduledTasksOpen(false);
                navigate(`/como-experts/${id}`);
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ComoExpertsChat;
